import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  SwapHoriz as TransferIcon,
  Inventory as InventoryIcon,
  Warehouse as WarehouseIcon,
  Store as StoreIcon,
  LocationCity as CityIcon,
  CheckCircle as ActiveIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Place as PlaceIcon,
  Clear as ClearIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import TableActionButton from "../../components/layout/TableActionButton";
import SortableTableCell from "../../components/layout/SortableTableCell";
import { useRBAC } from "../../hooks/useRBAC";
import { useTableSort } from "../../hooks/useTableSort";
import { branchAPI } from "../../services/branchAPI";
import warehouseAPI, {
  CreateWarehouseData,
  Warehouse,
  WarehouseType,
} from "../../services/warehouseAPI";
import { getApiErrorMessage } from "../../utils/apiError";
import { tokens } from "../../theme";

type WarehouseSortField =
  | "name"
  | "code"
  | "type"
  | "city"
  | "isActive"
  | "createdAt";

const EMPTY_FORM: CreateWarehouseData = {
  name: "",
  code: "",
  type: "WAREHOUSE",
  branchId: "",
  address: "",
  city: "",
  state: "",
  postalCode: "",
  phone: "",
  email: "",
  isActive: true,
  isDefault: false,
};

function getTypeColor(type?: string): {
  color: string;
  bg: string;
  label: string;
} {
  const t = String(type || "WAREHOUSE").toUpperCase();
  if (t === "STORE") {
    return {
      color: tokens.colors.secondary.light,
      bg: alpha(tokens.colors.secondary.light, 0.12),
      label: "Store",
    };
  }
  if (t === "OUTLET") {
    return {
      color: tokens.colors.dashPurple,
      bg: alpha(tokens.colors.dashPurple, 0.12),
      label: "Outlet Stock",
    };
  }
  return {
    color: tokens.colors.primary.dark,
    bg: alpha(tokens.colors.primary.main, 0.14),
    label: "Warehouse / Godown",
  };
}

const WarehousesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canCreate, canUpdate, canDelete, canRead } = useRBAC();
  const canCreateWarehouse = canCreate("warehouses") || canCreate("inventory");
  const canUpdateWarehouse = canUpdate("warehouses") || canUpdate("inventory");
  const canDeleteWarehouse = canDelete("warehouses") || canDelete("inventory");

  const { sortBy, sortOrder, handleSort, sortParams } =
    useTableSort<WarehouseSortField>("name", "asc");
  const onSort = (field: WarehouseSortField) =>
    handleSort(field, () => setPage(0));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [branchFilter, setBranchFilter] = useState<string>("all");
  const [cityFilter, setCityFilter] = useState<string>("");

  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailModal, setDetailModal] = useState<Warehouse | null>(null);
  const [editing, setEditing] = useState<Warehouse | null>(null);
  const [form, setForm] = useState<CreateWarehouseData>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<Warehouse | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Main list query with complete multi-filtering & sorting
  const { data, isLoading, error } = useQuery({
    queryKey: [
      "warehouses",
      page,
      rowsPerPage,
      debouncedSearch,
      cityFilter,
      typeFilter,
      statusFilter,
      branchFilter,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      warehouseAPI.getWarehouses({
        pageNo: page + 1,
        pageSize: rowsPerPage,
        ...sortParams,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(cityFilter.trim() ? { city: cityFilter.trim() } : {}),
        ...(typeFilter !== "all" ? { type: typeFilter } : {}),
        ...(statusFilter === "active" ? { isActive: true } : {}),
        ...(statusFilter === "inactive" ? { isActive: false } : {}),
        ...(branchFilter !== "all" ? { branchId: branchFilter } : {}),
      }),
    enabled: canRead("warehouses"),
  });

  // Query for KPI aggregates (all active warehouses)
  const { data: allWarehousesData } = useQuery({
    queryKey: ["warehouses", "kpi-metrics"],
    queryFn: () => warehouseAPI.getWarehouses({ pageSize: 500 }),
    enabled: canRead("warehouses"),
  });

  const { data: branchesData } = useQuery({
    queryKey: ["branches", "warehouse-form"],
    queryFn: () => branchAPI.getBranches({ pageSize: 200 }),
    enabled: dialogOpen || Boolean(canRead("warehouses")),
  });

  const warehouses = data?.data || [];
  const total = data?.total || 0;
  const branches = branchesData?.data || [];
  const allList = allWarehousesData?.data || warehouses;

  // KPI calculations
  const kpis = useMemo(() => {
    const totalCount = allWarehousesData?.total ?? total;
    let activeCount = 0;
    let storeCount = 0;
    let godownCount = 0;
    const citiesSet = new Set<string>();

    for (const w of allList) {
      if (w.isActive) activeCount++;
      const t = String(w.type || "").toUpperCase();
      if (t === "STORE" || t === "OUTLET") {
        storeCount++;
      } else {
        godownCount++;
      }
      if (w.city?.trim()) citiesSet.add(w.city.trim().toLowerCase());
    }

    return {
      total: totalCount,
      active: activeCount,
      godowns: godownCount,
      stores: storeCount,
      cities: citiesSet.size,
    };
  }, [allList, allWarehousesData, total]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: CreateWarehouseData = {
        ...form,
        name: form.name.trim(),
        code: form.code?.trim() || undefined,
        branchId: form.branchId || null,
        city: form.city?.trim() || undefined,
        state: form.state?.trim() || undefined,
        address: form.address?.trim() || undefined,
        postalCode: form.postalCode?.trim() || undefined,
        phone: form.phone?.trim() || undefined,
        email: form.email?.trim() || undefined,
      };
      if (editing) {
        return warehouseAPI.updateWarehouse(editing.id, payload);
      }
      return warehouseAPI.createWarehouse(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warehouses"] });
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      setAlert({
        type: "success",
        message: editing
          ? "Warehouse updated successfully"
          : "Warehouse created successfully",
      });
    },
    onError: (err: unknown) => {
      setAlert({ type: "error", message: getApiErrorMessage(err) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => warehouseAPI.deleteWarehouse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warehouses"] });
      setDeleteTarget(null);
      setAlert({
        type: "success",
        message: "Warehouse deactivated successfully",
      });
    },
    onError: (err: unknown) => {
      setAlert({ type: "error", message: getApiErrorMessage(err) });
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (wh: Warehouse) => {
    setEditing(wh);
    setForm({
      name: wh.name,
      code: wh.code,
      type: wh.type || "WAREHOUSE",
      branchId: wh.branchId || "",
      address: wh.address || "",
      city: wh.city || "",
      state: wh.state || "",
      postalCode: wh.postalCode || "",
      phone: wh.phone || "",
      email: wh.email || "",
      isActive: wh.isActive,
      isDefault: wh.isDefault,
    });
    setDialogOpen(true);
  };

  const hasActiveFilters =
    Boolean(search) ||
    Boolean(cityFilter) ||
    typeFilter !== "all" ||
    statusFilter !== "all" ||
    branchFilter !== "all";

  const clearAllFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setCityFilter("");
    setTypeFilter("all");
    setStatusFilter("all");
    setBranchFilter("all");
    setPage(0);
  };

  if (!canRead("warehouses")) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          You do not have permission to view warehouses.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Warehouses & Godowns"
        subtitle="Manage central godowns, storage facilities, and store inventory points"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Warehouse", to: "/inventory" },
          { label: "Warehouses" },
        ]}
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<TransferIcon />}
              onClick={() => navigate("/stock-transfers")}
            >
              Stock Transfers
            </Button>
            <Button
              variant="outlined"
              startIcon={<InventoryIcon />}
              onClick={() => navigate("/inventory")}
            >
              Outlet Stock
            </Button>
            {canCreateWarehouse && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreate}
              >
                Add warehouse
              </Button>
            )}
          </Stack>
        }
      />

      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getApiErrorMessage(error)}
        </Alert>
      )}

      {/* KPI Metric Cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={6} sm={3}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${tokens.colors.neutral[200]}`,
              borderRadius: tokens.radius.sm,
            }}
          >
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: tokens.radius.sm,
                    bgcolor: alpha(tokens.colors.primary.main, 0.12),
                    color: tokens.colors.primary.dark,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <WarehouseIcon fontSize="small" />
                </Box>
                <Box minWidth={0}>
                  <Typography variant="caption" color="text.secondary">
                    Total Facilities
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {kpis.total}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${tokens.colors.neutral[200]}`,
              borderRadius: tokens.radius.sm,
            }}
          >
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: tokens.radius.sm,
                    bgcolor: alpha(tokens.colors.success.main, 0.12),
                    color: tokens.colors.success.main,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ActiveIcon fontSize="small" />
                </Box>
                <Box minWidth={0}>
                  <Typography variant="caption" color="text.secondary">
                    Active Warehouses
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="success.main">
                    {kpis.active}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${tokens.colors.neutral[200]}`,
              borderRadius: tokens.radius.sm,
            }}
          >
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: tokens.radius.sm,
                    bgcolor: alpha(tokens.colors.secondary.light, 0.12),
                    color: tokens.colors.secondary.light,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <StoreIcon fontSize="small" />
                </Box>
                <Box minWidth={0}>
                  <Typography variant="caption" color="text.secondary">
                    Stores & Outlets
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {kpis.stores}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${tokens.colors.neutral[200]}`,
              borderRadius: tokens.radius.sm,
            }}
          >
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: tokens.radius.sm,
                    bgcolor: alpha(tokens.colors.dashBlue, 0.12),
                    color: tokens.colors.dashBlue,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CityIcon fontSize="small" />
                </Box>
                <Box minWidth={0}>
                  <Typography variant="caption" color="text.secondary">
                    Cities Covered
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {kpis.cities}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table Card with Search & Multi-Filters */}
      <DataListCard
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        searchPlaceholder="Search warehouse name, code, city…"
        filters={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ sm: "center" }}
            flexWrap="wrap"
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="WAREHOUSE">Warehouse / Godown</MenuItem>
                <MenuItem value="STORE">Store</MenuItem>
                <MenuItem value="OUTLET">Outlet Stock</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="inactive">Inactive Only</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Branch</InputLabel>
              <Select
                value={branchFilter}
                label="Branch"
                onChange={(e) => {
                  setBranchFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Branches</MenuItem>
                {branches.map((b: { id: string; name: string }) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="City"
              placeholder="Filter by city"
              value={cityFilter}
              onChange={(e) => {
                setCityFilter(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 130 }}
            />

            {hasActiveFilters && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<ClearIcon />}
                onClick={clearAllFilters}
                sx={{ minHeight: 32 }}
              >
                Reset
              </Button>
            )}
          </Stack>
        }
      >
        <TableContainer>
          <Table size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <SortableTableCell
                  label="Warehouse Name"
                  field="name"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
                <SortableTableCell
                  label="Code"
                  field="code"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
                <SortableTableCell
                  label="Type"
                  field="type"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
                <SortableTableCell
                  label="Location"
                  field="city"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
                <TableCell>Branch</TableCell>
                <TableCell>Contact</TableCell>
                <SortableTableCell
                  label="Status"
                  field="isActive"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
                <TableCell
                  align="right"
                  width={180}
                  sx={{ width: 180, maxWidth: 180, minWidth: 180, px: 0.5 }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 3, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      Loading warehouses…
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : warehouses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 4, textAlign: "center" }}>
                    <WarehouseIcon
                      sx={{ fontSize: 36, color: "text.disabled", mb: 1 }}
                    />
                    <Typography fontWeight={600} color="text.secondary">
                      No warehouses found
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {hasActiveFilters
                        ? "Try adjusting your search or active filters"
                        : "Click 'Add warehouse' to register your first godown or store"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                warehouses.map((wh) => {
                  const typeMeta = getTypeColor(wh.type);
                  return (
                    <TableRow key={wh.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            fontWeight={650}
                            sx={{
                              cursor: "pointer",
                              "&:hover": { color: "primary.main" },
                            }}
                            onClick={() => setDetailModal(wh)}
                          >
                            {wh.name}
                          </Typography>
                          {wh.isDefault && (
                            <Chip
                              icon={<StarIcon sx={{ fontSize: "14px !important" }} />}
                              label="Default"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 600, height: 20 }}
                            />
                          )}
                        </Stack>
                        {wh.address && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            noWrap
                            sx={{ maxWidth: 220 }}
                          >
                            {wh.address}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={wh.code}
                          size="small"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            borderRadius: tokens.radius.sm,
                            bgcolor: tokens.colors.neutral[100],
                            color: tokens.colors.neutral[800],
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={typeMeta.label}
                          size="small"
                          sx={{
                            fontWeight: 650,
                            bgcolor: typeMeta.bg,
                            color: typeMeta.color,
                            fontSize: "0.75rem",
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={500}>
                            {wh.city || "—"}
                          </Typography>
                          {wh.state && (
                            <Typography variant="caption" color="text.secondary">
                              {wh.state}
                              {wh.postalCode ? ` - ${wh.postalCode}` : ""}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {wh.branch?.name || (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              Company Central
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.25}>
                          {wh.phone ? (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <PhoneIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                              {wh.phone}
                            </Typography>
                          ) : null}
                          {wh.email ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <EmailIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                              {wh.email}
                            </Typography>
                          ) : null}
                          {!wh.phone && !wh.email && (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={wh.isActive ? "Active" : "Inactive"}
                          size="small"
                          sx={{
                            fontWeight: 650,
                            bgcolor: wh.isActive
                              ? tokens.colors.success.light
                              : tokens.colors.error.light,
                            color: wh.isActive
                              ? tokens.colors.success.main
                              : tokens.colors.error.main,
                          }}
                        />
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{ width: 180, maxWidth: 180, minWidth: 180, px: 0.5 }}
                      >
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                          sx={{ width: "100%" }}
                        >
                          <TableActionButton
                            title="View full details"
                            onClick={() => setDetailModal(wh)}
                          >
                            <ViewIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>

                          <TableActionButton
                            title="Transfer stock from here"
                            onClick={() =>
                              navigate(
                                `/stock-transfers?fromWh=${wh.id}&create=1`
                              )
                            }
                          >
                            <TransferIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>

                          <TableActionButton
                            title="View stock in this warehouse"
                            onClick={() =>
                              navigate(`/inventory?warehouseId=${wh.id}`)
                            }
                          >
                            <InventoryIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>

                          {canUpdateWarehouse && (
                            <TableActionButton
                              title="Edit warehouse"
                              variant="primary"
                              onClick={() => openEdit(wh)}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </TableActionButton>
                          )}

                          {canDeleteWarehouse && (
                            <TableActionButton
                              title="Deactivate warehouse"
                              variant="danger"
                              onClick={() => setDeleteTarget(wh)}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </TableActionButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </DataListCard>

      {/* Warehouse Detail Modal */}
      <Dialog
        open={Boolean(detailModal)}
        onClose={() => setDetailModal(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {detailModal?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Code: <strong>{detailModal?.code}</strong>
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              {detailModal?.isDefault && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: "14px !important" }} />}
                  label="Default"
                  size="small"
                  color="primary"
                />
              )}
              <Chip
                label={detailModal?.isActive ? "Active" : "Inactive"}
                size="small"
                color={detailModal?.isActive ? "success" : "default"}
              />
              <IconButton size="small" onClick={() => setDetailModal(null)} sx={{ ml: 1 }}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2, maxHeight: "72vh" }}>
          {detailModal && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  Facility Type & Organization
                </Typography>
                <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Type:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {getTypeColor(detailModal.type).label}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Linked Branch:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {detailModal.branch?.name || "Company Central (All branches)"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  Address & Location
                </Typography>
                <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <PlaceIcon
                        sx={{ fontSize: 16, mr: 0.5, verticalAlign: "text-bottom", color: "text.secondary" }}
                      />
                      {detailModal.address || "No street address provided"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      City:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {detailModal.city || "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      State:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {detailModal.state || "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Postal Code:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {detailModal.postalCode || "—"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  Contact Information
                </Typography>
                <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Phone:
                    </Typography>
                    <Typography variant="body2">
                      {detailModal.phone || "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Email:
                    </Typography>
                    <Typography variant="body2">
                      {detailModal.email || "—"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: tokens.colors.neutral[50] }}>
          <Button
            size="small"
            startIcon={<TransferIcon />}
            variant="outlined"
            onClick={() => {
              if (detailModal) {
                navigate(`/stock-transfers?fromWh=${detailModal.id}&create=1`);
              }
            }}
          >
            Transfer from here
          </Button>
          <Button
            size="small"
            startIcon={<InventoryIcon />}
            variant="outlined"
            onClick={() => {
              if (detailModal) {
                navigate(`/inventory?warehouseId=${detailModal.id}`);
              }
            }}
          >
            View Stock
          </Button>
          <Button size="small" onClick={() => setDetailModal(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Warehouse Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              {editing ? `Edit Warehouse (${editing.code})` : "Add New Warehouse / Facility"}
            </Typography>
            <IconButton size="small" onClick={() => setDialogOpen(false)}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "72vh" }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Warehouse Name"
                fullWidth
                required
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="e.g. Central Godown or Store A"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Code"
                fullWidth
                value={form.code}
                onChange={(e) =>
                  setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))
                }
                helperText="Auto-slug if blank"
                placeholder="e.g. WH-MAIN"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={form.type || "WAREHOUSE"}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      type: e.target.value as WarehouseType,
                    }))
                  }
                >
                  <MenuItem value="WAREHOUSE">Warehouse / Godown</MenuItem>
                  <MenuItem value="STORE">Store Facility</MenuItem>
                  <MenuItem value="OUTLET">Outlet Stock Location</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Branch</InputLabel>
                <Select
                  label="Branch"
                  value={form.branchId || ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, branchId: e.target.value }))
                  }
                >
                  <MenuItem value="">Company Global (All Branches)</MenuItem>
                  {branches.map((b: { id: string; name: string }) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                fullWidth
                value={form.city}
                onChange={(e) =>
                  setForm((f) => ({ ...f, city: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="State"
                fullWidth
                value={form.state}
                onChange={(e) =>
                  setForm((f) => ({ ...f, state: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                multiline
                minRows={2}
                value={form.address}
                onChange={(e) =>
                  setForm((f) => ({ ...f, address: e.target.value }))
                }
                placeholder="Street address / locality"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Postal code"
                fullWidth
                value={form.postalCode}
                onChange={(e) =>
                  setForm((f) => ({ ...f, postalCode: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Phone"
                fullWidth
                value={form.phone}
                onChange={(e) =>
                  setForm((f) => ({ ...f, phone: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={form.email}
                onChange={(e) =>
                  setForm((f) => ({ ...f, email: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.isDefault)}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, isDefault: e.target.checked }))
                    }
                  />
                }
                label="Company Default Warehouse"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ ml: 4, mt: -0.5 }}
              >
                Primary warehouse for auto stock transfers & backfill
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive !== false}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, isActive: e.target.checked }))
                    }
                  />
                }
                label="Active Status"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.name.trim() || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {editing ? "Save changes" : "Create warehouse"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirmation dialog */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>Deactivate warehouse?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate{" "}
            <strong>{deleteTarget?.name}</strong> ({deleteTarget?.code})?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Inactive warehouses will not be selectable for new transfers or default inventory.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={() =>
              deleteTarget && deleteMutation.mutate(deleteTarget.id)
            }
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WarehousesPage;
